import { getLang } from 'config/lang'
import { Granularities } from 'constants/granularities'
import {
  addDays,
  addHours,
  addYears,
  format,
  fromUnixTime,
  isValid,
  lastDayOfYear,
  startOfWeek,
  subYears,
} from 'date-fns'
import { DateFormat } from 'interfaces/components.interfaces'
import { months } from 'locale/date'
import type { Moment } from 'moment/moment'

export const toDate = (date: Date | number | string) => {
  let dateMessage
  if (date instanceof Date) {
    dateMessage = date
  } else if (typeof date === 'string') {
    dateMessage = new Date(date)
  } else {
    dateMessage = fromUnixTime(date.toString().length > 11 ? date / 1000 : date)
  }
  return dateMessage
}

export const getFormattedDate = (date?: DateFormat) => {
  const defaultValue = {
    date: undefined,
    formatDate: '',
    formatTime: '',
    formatTimeDate: '',
    formatDateMonth: '',
    formatDateTime: '',
  }

  if (!date) {
    return defaultValue
  }

  const dateMessage = toDate(date)

  if (!isValid(dateMessage)) {
    return defaultValue
  }

  const formatTime = format(dateMessage, 'HH:mm')
  const formatDate = format(dateMessage, 'dd.MM.uuuu')
  const formatDateMonth =
    format(dateMessage, 'd ') + months[getLang()][dateMessage.getMonth()][1] + format(dateMessage, ' uuuu')
  const formatTimeDate = `${formatTime}, ${formatDate}`
  const formatDateTime = `${formatDate}, ${formatTime}`

  return { formatDate, formatTime, formatTimeDate, formatDateTime, formatDateMonth, date: dateMessage }
}

export const formatDateTime = (date?: DateFormat) => {
  if (!date) {
    return ''
  }
  const dateMessage = toDate(date)
  if (!isValid(dateMessage)) {
    return ''
  }
  return format(dateMessage, 'dd.MM.uuuu HH:mm')
}

export const getCurrentYear = (): string => {
  const now = new Date()
  return format(now, 'yyyy')
}

export const dateToServer = (date: Date | number | string) => format(new Date(date), 'uuuu-MM-dd')

export const getFirstDayYear = (year: number | string) => new Date(Number(year), 0, 1)
export const getLastDayYear = (year: number | string) => lastDayOfYear(getFirstDayYear(year))

export const toMidnight = (date: Date | string | number) => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

export const roundToDay = (date: Date | string | number): Date => {
  const d = new Date(date)
  if (d.getHours() >= 12) {
    d.setDate(d.getDate() + 1)
  }
  d.setHours(0, 0, 0, 0)
  return d
}

export const getTodayDate = () => toMidnight(new Date())
export const getTodayTimestamp = () => toMidnight(new Date()).getTime()
export const getTomorrowDate = () => toMidnight(addDays(new Date(), 1))
export const getTomorrowTimestamp = () => getTomorrowDate().getTime()

export const addYear = (date: Date | string | number, granularityCode?: Granularities | null, years = 1) => {
  if (granularityCode === Granularities.Weeks) {
    return startOfWeek(addYears(toMidnight(new Date(date)), years), { weekStartsOn: 1 })
  }
  return addYears(toMidnight(new Date(date)), years)
}

export const subYear = (date: Date | string | number, granularityCode?: Granularities | null, years = 1) => {
  if (granularityCode === Granularities.Weeks) {
    return startOfWeek(subYears(toMidnight(new Date(date)), years), { weekStartsOn: 1 })
  }
  return subYears(toMidnight(new Date(date)), years)
}

export const datesToTimestamp = (dates: Date[]): number[] => dates.map((date) => date.getTime())

export const isValidDate = (value?: string | Date | null): value is string | Date => {
  if (!value) {
    return false
  }
  return !Number.isNaN(new Date(value).getTime())
}

export const disabledDate = (
  disabledBeforeToday?: boolean,
  disabledBeforeDate?: Date | string | number | null,
): ((current?: Moment) => boolean) | undefined => {
  if (!disabledBeforeToday && !disabledBeforeDate) {
    return
  }
  return (current?: Moment) =>
    !!current &&
    current.valueOf() < toMidnight(disabledBeforeDate ? new Date(disabledBeforeDate).getTime() : new Date()).getTime()
}

export const dateForFiltration = (date = new Date()) => format(addHours(date, -2), 'yyyy-MM-dd')
export const dateForDefaultTitle = (date = new Date()) => format(addHours(date, -2), 'yyyy.MM.dd').replace(/[.]/g, '')

export const dateFromNumberString = (string?: string) =>
  string ? format(new Date(`${string.slice(0, 4)}.${string?.slice(4, 6)}.${string?.slice(6)}`), 'uuuu.MM.dd') : ''
